import {useCallback, useState} from 'react';
import {observer} from 'mobx-react';
import styles from './style.module.scss';
import {
  Divider,
  Row,
  Popover,
  Tooltip,
  Spin,
} from 'antd';
import classnames from 'classnames';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck, faFilePlus, faMessageCheck, faCopy} from '@fortawesome/pro-regular-svg-icons';
import {useTranslation} from 'next-i18next';
import {QuestionModel} from '@/store/content-optimizer/CurrentPageModel';
import {useStore} from '@/store/root-store';
import {LoadingOutlined} from '@ant-design/icons';
import {costPerClickFormatter, searchVolumeFormatter} from '../../../../../LeftSide/EditorSection/content-editor/utils';
import {debounce, isNil} from 'lodash';
import {insertText} from '@/utils/editor-scripts';
import {EditorState} from 'draft-js';

interface Props {
  question: QuestionModel;
  i: number | string;
}


export const SingleQuestion = observer(({question, i}: Props) => {
  const {contentOptimizer: {
    contentFragment,
  },
  } = useStore('');
  const {contentOptimizer: {currentPage, aiOutline}} = useStore('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [aiCopied, setAiCopied] = useState(false);
  const [creatingPage, setCreatingPage] = useState(false);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const {t} = useTranslation('common');

  const antIcon = <LoadingOutlined style={{fontSize: 14, color: '#7f4ead', marginRight: '10px'}} spin />;

  const fetchAndInsertText = async (action: 'insert' | 'copy') => {
    try {
      setIsAiGenerating(true);
      await contentFragment.fetchFragment(`${question.keyword}?`);

      if (action === 'copy') {
        if (contentFragment?.fragment?.results) {
          if (isNil(contentFragment?.fragment?.results[0])) return;
          navigator.clipboard.writeText(`\n${question.keyword}?\n\n${contentFragment?.fragment?.results[0]}`);
          setAiCopied(true);
          setIsAiGenerating(false);

          setTimeout(() => {
            setAiCopied(false);
          }, 5000);
        }
      }
    } catch (e) {
      Promise.reject(e);
      setIsAiGenerating(false);
    }
  };

  const debouncedChange = debounce((state: EditorState, focusTerms, focusTermsClassNames, isNewDecorations = false) => {
    // we are then calling the edit content method which will fire the API call and send new state to the BE
    currentPage.editContent(state, isNewDecorations);
  }, 2000);


  const debouncedUpdate = useCallback(
    (state: EditorState, focusTerms, focusTermsClassNames, isNewDecorations) => debouncedChange(state, focusTerms, focusTermsClassNames, isNewDecorations),
    // eslint-disable-next-line
        []
  );

  return <Popover
    overlayClassName={styles.focusTermPopover}
    trigger='hover'
    placement='left'
    autoAdjustOverflow={true}
    content={
      <div>
        <h5 className={styles.popOverTitle}>{t('question')}</h5>
        <h5>{question.keyword ? question.keyword : null}</h5>
        <div style={{marginTop: '5px'}}>
          {question?.sv !== undefined && question?.sv !== null && <span style={{marginRight: '20px', fontSize: '12px', color: '#e8e8e8'}}>{`SV: ${searchVolumeFormatter(question.sv)}`}</span>}
          {question?.cpc !== undefined && question?.cpc !== null && <span style={{fontSize: '12px', color: '#e8e8e8'}}>{`CPC: ${costPerClickFormatter(question.cpc)}`}</span>}
        </div>
        <Divider className={styles.popoverDivider} />
        <h5 className={styles.popOverTitle}>{t('number-of-uses')}</h5>
        <p>
          <span>{question?.usedCount ?? ''}</span>
        </p>
      </div>
    }
  >
    <Row key={`${question}-${i}`} justify='space-between' className={styles.focusTermContent} style={{marginBottom: '8px'}}>
      <div className={styles.focusTermAndProgressContainer} style={{justifyContent: 'space-between'}}>
        <div className={classnames(styles.focusTermNameAndCopy, styles.questionsNameAndCopy)} onClick={() => {
          navigator.clipboard.writeText(`${question?.keyword ?? ''}?`);
        }}>
          <div className={classnames(styles.focusTermName, styles.customQuestionsName)}>
            {question?.keyword ?? ''}
            <Tooltip
              title={copySuccess ? <span><FontAwesomeIcon icon={faCheck} style={{color: '#2AC155'}}/>{` Copied to clipboard`}</span> : t('copy-to-clipboard')}
              placement='bottom'
              overlayClassName={styles.copyTooltip}
              onVisibleChange={visible => {
                if (!visible) {
                  setTimeout(() => {
                    setCopySuccess(false);
                  }, 300);
                }
              }}>
              <div className={styles.copyIcon} onClick={() => {
                navigator.clipboard.writeText(`${question?.keyword ?? ''}?`);
                setCopySuccess(true);
              }}>
                <FontAwesomeIcon icon={faCopy} style={{marginRight: 'auto', marginLeft: 5, fontSize: 14, marginBottom: -1}}/>
              </div>
            </Tooltip>
            <Tooltip
              title={!isAiGenerating ?
                aiCopied ? <span><FontAwesomeIcon icon={faCheck} style={{color: '#2AC155'}}/>{`Answer Copied to Clipboard`}</span> : `Ai Generate Answer` :
                <span><Spin indicator={antIcon} />{`Ai Generating Answer`}</span>}
              placement='top'
              overlayClassName={styles.copyTooltip}
              onVisibleChange={visible => {
                if (!visible) {
                  setTimeout(() => {
                    setCopySuccess(false);
                  }, 300);
                }
              }}>
              <div className={styles.copyIcon} onClick={e => {
                e.stopPropagation();
                fetchAndInsertText('copy');
              }}>
                <FontAwesomeIcon icon={faMessageCheck} style={{marginRight: 'auto', marginLeft: 5, fontSize: 14, marginBottom: -1}}/>
              </div>
            </Tooltip>
            <Tooltip title={creatingPage ? <span><Spin indicator={antIcon} />Adding to the article...</span>: 'Add to the article.'}>
              <div className={styles.copyIcon} style={creatingPage ? {pointerEvents: 'none', cursor: 'not-allowed'} : {}} onClick={e => {
                if (creatingPage) return;
                e.stopPropagation();
                setCreatingPage(true);
                insertText(`<p></p>` + question?.keyword + '? ', Number(i), currentPage, aiOutline, debouncedUpdate);
                setCreatingPage(false);
                // onCreatePage();
              }}>
                <FontAwesomeIcon icon={faFilePlus} style={{marginLeft: 5, marginBottom: -1, fontSize: 14}}/>
              </div>
            </Tooltip>
          </div>

          <FontAwesomeIcon icon={faCheck} style={{color: question?.usedCount ? '#1FAC47' : '#4E5156', marginLeft: 'auto', marginRight: '10px'}}/>
        </div>
      </div>
    </Row>
  </Popover>;
});
